import * as React from 'react';
import { FixMeLater } from '../../utils';
import {
    HoneycombShadowSize,
    NotificationColor,
    NotificationCornerStyle,
    NotificationVariant,
    ResponsiveStyleValue,
    useComponentDefaultProps,
} from '../../theming';
import { Styler, StylerProps } from '../Styler/Styler';
import { useStyles } from './Notification.styles';
import { SvgIconProps } from '../SvgIcon/SvgIcon';
import { CrossIcon, IconButton } from '../..';

export interface NotificationProps extends StylerProps {
    /**
     * The contents of the notification.
     */
    children?: React.ReactNode;

    /**
     * The notification's corner style. It might be preferable to set this on the theme using default props.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default square
     */
    cornerStyle?: ResponsiveStyleValue<NotificationCornerStyle>;

    /**
     * The colour of the notification.
     * @default neutral
     */
    color?: NotificationColor;

    /**
     * An svg icon component. The icon will be displayed before the content.
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * Callback fired when the component requests to be dismissed.
     * If provided, the component will be have a `dismiss` button.
     * @param {React.SyntheticEvent} event The event source of the callback.
     */
    onDismiss?: (event: React.SyntheticEvent) => void;

    /**
     * The URL that the clickable notification will navigate to. If provided, the notification will be rendered as an anchor.
     */
    href?: string;

    /**
     * The style variant.
     * @default default
     */
    variant?: ResponsiveStyleValue<NotificationVariant>;

    /**
     * A shadow from the theme.
     */
    shadow?: HoneycombShadowSize;
}

const defaultProps: Partial<NotificationProps> = {
    color: 'info',
    cornerStyle: 'square',
    variant: 'default',
    shadow: 'none',
};

/**
 * The Notification component is a UI component used to notify the user of anything, it can pop up on the screen in different colours and variants
 * to grab the attention of the user and to notify them of any messages. The notifications can be always displayed or closable, which allows the user to
 * close the notification if they have addressed it. Notification can be configured with multiple corner styles and variants via input props to dictate what
 * form of notification is shown to the user.
 */
export const Notification: React.ForwardRefExoticComponent<NotificationProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function Notification(
        props: NotificationProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const {
            children,
            color = 'info',
            cornerStyle = 'square',
            icon,
            onClick,
            onDismiss,
            href,
            variant = 'default',
            shadow = 'none',
            ...other
        } = useComponentDefaultProps('Notification', defaultProps, props);

        const { styles } = useStyles({
            color,
            cornerStyle,
            variant,
            shadow,
        });

        const dismissible = typeof onDismiss === 'function';

        const handleDismissClick = (event: React.SyntheticEvent) => {
            event.stopPropagation();
            onDismiss?.(event);
        };

        return (
            <Styler ref={ref} baseStyles={styles.root} data-notification={true} {...other}>
                <div css={styles.inner} data-inner={true}>
                    {icon &&
                        React.cloneElement(icon, {
                            css: styles.icon,
                            'data-icon': 'true',
                        })}
                    <div css={styles.content} data-content={true}>
                        {children}
                    </div>
                </div>
                {dismissible && (
                    <IconButton size="xs" color="black" onClick={handleDismissClick} alignEdge="end">
                        <CrossIcon />
                    </IconButton>
                )}
            </Styler>
        );
    });
